import classnames from 'classnames';
import { stringify } from 'qs';
import type { CSSProperties } from 'react';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import { useHarmonicIntervalFn } from 'react-use';
import styled from 'styled-components';

import type { Keys, Salt } from '@sorare/wallet-shared';

import { ORIGIN, WALLET_URL } from '#src/config.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import {
  useMessagingContext,
  useWalletContext,
} from '#src/contexts/wallet/index.ts';
import { useFetchEncryptedPrivateKeyMutation } from '#src/hooks/auth/useFetchEncryptedPrivateKeyMutation.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { getSalt } from '#src/lib/password.ts';
import { theme } from '#src/style/theme.tsx';

import {
  useChangePassword,
  useForgotPassword,
  useGetPasskeyEncryptionKey,
  useInit,
  usePrepareEthDeposit,
  usePromptAddFunds,
  usePromptAddFundsEth,
  usePromptRestoreWallet,
  useRecoverKey,
  useRegisterGenerateKeys,
  useSignIn,
  useSignInWithPasskey,
  useSignUp,
  useTransaction,
} from './handlers/index.ts';
import { useErrorEvent } from './handlers/useErrorEvent.ts';
import { usePromptVerifyPhone } from './handlers/usePromptVerifyPhone.ts';

const key = 'wallet';
const MISSING_OTP_CODE = 1000;

type FrameStyle = Pick<CSSProperties, 'width' | 'height' | 'top' | 'left'> & {
  '--header-size'?: string;
};

const defaultStyle: FrameStyle = {
  width: '0px',
  height: '0px',
  top: 0,
  left: 0,
};

type ListenerSpec = {
  eventSource: (walletNode: Element) => EventTarget | null;
  eventName: string;
};

type RegistrationHandle = {
  unregister: () => void;
};

const listenerSpecs: readonly ListenerSpec[] = [
  {
    // https://tkte.ch/articles/2019/09/23/iOS-VisualViewport.html
    eventSource: () => window.visualViewport || window,
    eventName: 'resize',
  },
  {
    eventSource: () => window,
    eventName: 'scroll',
  },
  {
    eventSource: walletNode => walletNode,
    eventName: 'styleChanged',
  },
  // If the iframe is in a dialog, the scroll event is
  // on the parent presentation, not the body
  {
    eventSource: walletNode => walletNode.closest('[role~="presentation"]'),
    eventName: 'scroll',
  },
] as const;

const plugListeners = (
  walletNode: Element,
  eventListenersTargetSpec: readonly ListenerSpec[],
  eventListener: EventListener
): RegistrationHandle[] => {
  return listenerSpecs.reduce<RegistrationHandle[]>(
    (
      listeners: RegistrationHandle[],
      { eventName, eventSource }: ListenerSpec
    ) => {
      const target = eventSource(walletNode);
      if (target) {
        target.addEventListener(eventName, eventListener);
        listeners.push({
          unregister: () =>
            target.removeEventListener(eventName, eventListener),
        });
      }
      return listeners;
    },
    []
  );
};

const unregisterRegistrationHandles = (registrations: RegistrationHandle[]) => {
  registrations.forEach(({ unregister }) => {
    unregister();
  });
};

const Root = styled.iframe`
  --header-size: 60;
  position: absolute;
  border: none;
  width: 100%;
  max-height: calc(100% - var(--header-size) * 1px);
  border-radius: 8px;
  z-index: ${theme.zIndex.modal + 1};
  &.hideFrame {
    display: none !important;
  }
`;

/**
 * This wraps the wallet iframe. It is added only once to the page so that the iframe is only loaded once.
 * It is not possible to move an iframe in the DOM without triggering a reloading so it prevents us from using a Portal to display the iframe where it is needed. Using the shadow DOM does not work either for the same reasons.
 * The iframe is positioned absolutely and displayed wherever it is needed by using the WalletPlaceholder Component and mimicking its size and position.
 */
export const Frame = () => {
  const { registerHandler } = useMessagingContext();
  const { sorareEncryptionKey } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const iFrame = useRef<HTMLIFrameElement | null>(null);
  const [frameStyle, setFrameStyle] = useState<FrameStyle>(defaultStyle);
  const [fetchEncryptedPrivateKey] = useFetchEncryptedPrivateKeyMutation();
  const { isAndroidApp, postMessage } = useIsMobileApp();

  const { setWindow, walletNode } = useWalletContext();

  useInit();
  useErrorEvent();
  useSignIn();
  useSignInWithPasskey();
  useSignUp();
  useChangePassword();
  useForgotPassword();
  usePrepareEthDeposit();
  usePromptRestoreWallet();
  useRecoverKey();
  usePromptAddFundsEth();
  usePromptAddFunds();
  usePromptVerifyPhone();
  useTransaction();
  useRegisterGenerateKeys();
  useGetPasskeyEncryptionKey();

  useEffect(() => {
    const window = iFrame.current
      ? iFrame.current.contentWindow || undefined
      : undefined;

    setWindow(window);
  }, [setWindow]);

  useEffect(
    () =>
      registerHandler<Salt>('salt', async ({ email }) => {
        const salt = await getSalt(email);
        return { result: { salt } };
      }),
    [registerHandler]
  );

  useEffect(
    () =>
      registerHandler<Keys>('keys', async () => {
        if (!currentUser) return { result: { sorareEncryptionKey } };
        const { sorarePrivateKey, otpRequiredForLogin } = currentUser;
        if (otpRequiredForLogin) {
          const result = await fetchEncryptedPrivateKey({
            variables: { input: {} },
          });
          if (
            result?.data?.fetchEncryptedPrivateKey?.errors.some(
              error => error.code === MISSING_OTP_CODE
            )
          )
            return {
              result: {
                userPrivateKey: undefined,
                sorareEncryptionKey,
              },
              error: 'invalid-otp',
            };
          return {
            result: {
              userPrivateKey:
                result.data?.fetchEncryptedPrivateKey?.sorarePrivateKey ||
                undefined,
              sorareEncryptionKey,
            },
          };
        }
        return {
          result: {
            userPrivateKey: sorarePrivateKey || undefined,
            sorareEncryptionKey,
          },
        };
      }),
    [
      currentUser,
      registerHandler,
      fetchEncryptedPrivateKey,
      sorareEncryptionKey,
    ]
  );

  const computeFrameStyles = () => {
    if (walletNode) {
      const computedStyles = getComputedStyle(walletNode);
      const headerSize = computedStyles.getPropertyValue('--header-size');
      const rect = walletNode.getBoundingClientRect();

      setFrameStyle({
        '--header-size': headerSize,
        width: computedStyles.width,
        height:
          computedStyles.height === '0px' ? '100%' : computedStyles.height,
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    } else {
      setFrameStyle(defaultStyle);
    }
  };

  // WORK AROUND TO ENSURE THE IFRAME STICK TO THE PLACEHOLDER
  useHarmonicIntervalFn(computeFrameStyles, walletNode ? 500 : null);

  const registerHandles = useEffectEvent((walletNodeArg: Element) => {
    const registrationHandles = plugListeners(
      walletNodeArg,
      listenerSpecs,
      computeFrameStyles
    );
    computeFrameStyles();
    return registrationHandles;
  });
  useEffect(() => {
    if (walletNode) {
      // oxlint-disable-next-line react/set-state-in-effect
      const registrationHandles = registerHandles(walletNode);
      return () => {
        unregisterRegistrationHandles(registrationHandles);
      };
    }
    return () => {};
  }, [walletNode]);

  const onLoadCallback = useCallback(() => {
    if (isAndroidApp) {
      postMessage('loaded', {
        app: undefined,
        page: undefined,
        iframe: {
          url: WALLET_URL,
        },
      });
    }
  }, [isAndroidApp, postMessage]);

  return (
    <Root
      ref={iFrame}
      onLoad={onLoadCallback}
      title={key}
      key={key}
      src={`${WALLET_URL}?${stringify({
        allowedOrigin: ORIGIN,
      })}`}
      id={key}
      className={classnames({
        hideFrame: !walletNode,
      })}
      style={frameStyle}
      sandbox={[
        'allow-same-origin allow-scripts allow-forms allow-popups allow-downloads',
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#allow-popups-to-escape-sandbox
        'allow-popups-to-escape-sandbox',
      ].join(' ')}
    />
  );
};
